"use client";

import React from "react";
import { ArrowRight, Building2, ChevronRight, MapPin, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const leaders = [{
  id: "l1",
  name: "Michael Penrod",
  role: "Principal Owner & Visionary",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/leader_michael_penrod.png",
  bio: "Driving the expansion of the Elbo Room legacy into a modern media and hospitality empire."
}, {
  id: "l2",
  name: "Sarah Jenkins",
  role: "Director of Development",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/leader_sarah_jenkins.png",
  bio: "Spearheading architectural innovations and new property acquisitions across Fort Lauderdale."
}, {
  id: "l3",
  name: "David Chen",
  role: "Head of Media Operations",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/leader_david_chen.png",
  bio: "Architecting the digital infrastructure for Beach Radio and the automated news engine."
}];
const projects = [{
  id: "p1",
  title: "Elbo Room Social Club",
  status: "Phase 2 Construction",
  location: "Fort Lauderdale Beach",
  specs: "12,500 SQ FT | Mixed-Use Hospitality",
  description: "A premium extension of the iconic brand, featuring exclusive lounges, private event spaces, and integrated live broadcasting booths overlooking the ocean.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/project_social_club.png",
  link: "/CorporatePage"
}, {
  id: "p2",
  title: "Beach Radio HQ & Studios",
  status: "Blueprint Finalization",
  location: "Downtown Arts District",
  specs: "8,000 SQ FT | Media Production",
  description: "State-of-the-art production facility housing the daily live shows, automated news engine servers, and corporate offices for the family of companies.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/project_radio_hq.png",
  link: "/CorporatePage"
}];
export default function LeadershipAndProjects() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const leadersReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const projectsReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="leadership-projects" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)]" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        {}
        <div ref={headerReveal.ref} className={`mb-16 max-w-3xl transition-all duration-1000 ${headerReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mb-4 flex items-center gap-2">
            <div className="h-[1px] w-12 bg-primary" />
            <span className="font-mono text-sm font-bold tracking-widest text-primary uppercase">
              Corporate & Vision
            </span>
          </div>
          <h2 className="font-serif text-5xl tracking-tight text-white md:text-6xl lg:text-7xl">
            Leadership & <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">Projects</span>
          </h2>
          <p className="mt-6 text-lg text-muted-foreground md:text-xl">
            Meet the minds driving the Elbo Room family of companies forward. From iconic hospitality to cutting-edge media infrastructure, our leadership team is building the definitive future of Fort Lauderdale.
          </p>
        </div>

        {}
        <div ref={leadersReveal.ref} className={`mb-24 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 transition-all duration-1000 delay-200 ${leadersReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          {leaders.map((leader, i) => <div key={leader.id} data-index={i} className="group relative overflow-hidden rounded-xl border border-border/50 bg-card/30 backdrop-blur-sm transition-all duration-500 hover:border-primary/50 hover:shadow-[0_0_30px_-10px_rgba(245,158,11,0.3)]">
              <div className="aspect-[3/4] w-full overflow-hidden bg-muted">
                <img src={leader.image} alt={leader.name} className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent opacity-90 transition-opacity duration-500 group-hover:opacity-100" />
              
              <div className="absolute bottom-0 left-0 w-full p-6 translate-y-4 transition-transform duration-500 group-hover:translate-y-0">
                <div className="mb-2 font-mono text-xs font-semibold tracking-widest text-primary uppercase">
                  {leader.role}
                </div>
                <h3 className="mb-2 font-serif text-3xl text-white">{leader.name}</h3>
                <p className="text-sm text-muted-foreground opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  {leader.bio}
                </p>
              </div>
            </div>)}
        </div>

        {}
        <div ref={projectsReveal.ref} className={`transition-all duration-1000 delay-400 ${projectsReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mb-10 flex items-center justify-between">
            <h3 className="font-serif text-4xl text-white md:text-5xl">Project Spotlight</h3>
            <Button variant="outline" className="hidden md:flex border-primary/30 text-primary hover:bg-primary/10" asChild>
              <Link to="/CorporatePage">
                View All Developments <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-8">
            {projects.map((project, i) => <Card key={project.id} data-index={i} className="group overflow-hidden border-border/40 bg-card/20 backdrop-blur-md transition-all duration-500 hover:border-primary/40 hover:bg-card/40">
                <div className="flex flex-col md:flex-row">
                  {}
                  <div className="relative h-64 w-full overflow-hidden md:h-auto md:w-2/5 lg:w-1/2">
                    <div className="absolute inset-0 z-10 bg-primary/10 mix-blend-overlay transition-opacity duration-500 group-hover:opacity-0" />
                    <img src={project.image} alt={project.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    {}
                    <div className="pointer-events-none absolute inset-0 z-20 bg-[linear-gradient(to_right,#3b82f620_1px,transparent_1px),linear-gradient(to_bottom,#3b82f620_1px,transparent_1px)] bg-[size:20px_20px] opacity-30 mix-blend-screen transition-opacity duration-500 group-hover:opacity-10" />
                  </div>

                  {}
                  <CardContent className="flex flex-col justify-center p-8 md:w-3/5 lg:w-1/2 lg:p-12">
                    <div className="mb-6 flex flex-wrap items-center gap-4 font-mono text-xs tracking-wider text-muted-foreground uppercase">
                      <span className="flex items-center gap-1.5 text-primary">
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-primary"></span>
                        </span>
                        {project.status}
                      </span>
                      <span className="hidden h-1 w-1 rounded-full bg-border md:block" />
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" /> {project.location}
                      </span>
                    </div>

                    <h4 className="mb-4 font-serif text-3xl text-white md:text-4xl">
                      {project.title}
                    </h4>
                    
                    <p className="mb-8 text-muted-foreground md:text-lg">
                      {project.description}
                    </p>

                    <div className="mt-auto flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between border-t border-border/50 pt-6">
                      <div className="flex items-center gap-2 font-mono text-sm text-muted-foreground">
                        <Ruler className="h-4 w-4 text-primary" />
                        {project.specs}
                      </div>
                      <Button variant="ghost" className="w-fit group/btn text-white hover:text-primary hover:bg-transparent p-0" asChild>
                        <Link to={project.link}>
                          View Schematics 
                          <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </div>
              </Card>)}
          </div>
          
          <div className="mt-8 flex justify-center md:hidden">
            <Button variant="outline" className="w-full border-primary/30 text-primary hover:bg-primary/10" asChild>
              <Link to="/CorporatePage">
                View All Developments <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}